import type { ChatMessage } from "../model/chat-types";
import { TermRow } from "../term/term-row";
import { added, meta, node, removed } from "./ansi";
import {
  HARNESS_14925,
  HarnessItem,
  HarnessUnit,
} from "./harness-14925";
import { HARNESS_14925_FULL } from "./harness-14925-full";
import { INSPECTOR_CODEX_HISTORY } from "./codex-history";

// The /dev/assets/chat playground hydrates from tsh harness data and nothing
// else — real turns, in the two wire forms tsh has:
//
//   semantic   the harness records (chat-harness-14925.ts): a unit's prompt
//              plus its folded message/tool-call/diff/plan items, converted
//              here into ChatMessages block by block. This is the form the
//              debug register prints raw.
//   rendered   the rows tsh itself painted (tsh render), CANONICALIZED back
//              into the semantic form. The rows carry a TUI's presentation —
//              unit rules, composer/marker glyphs, prose hard-wrapped to the
//              pane's 44 columns — and presentation is exactly what a look
//              must own: fed raw to a bubble look, every turn was a wall of
//              wrapped monospace (found on the playground). So the grammar
//              is folded out — glyphs cut, wraps rejoined, rules retired to
//              message identity — and only genuinely terminal rows (a `$`
//              command and its output) stay terminal blocks. The same
//              conversation then reads native under every look: prose
//              bubbles on chatgpt, rows again on the tui registers.
//
// The conversion is a rendering, not an edit: every string in a block is the
// wire's own; the only authored bytes are the SGR roles (the same accent /
// added / removed vocabulary the tsh page uses) and the plan glyphs.
export namespace ChatFixtures {
  function $prompt(unit: HarnessUnit, index: number): ChatMessage {
    return {
      id: `u${unit.unit}-prompt`,
      role: "user",
      content: [{ type: "text", text: unit.prompt ?? "" }],
      isStreaming: false,
      createdAt: unit.ts ? Date.parse(unit.ts) : index,
    };
  }

  function diffLines(item: HarnessItem & { kind: "diff" }): string[] {
    const lines: string[] = [];
    for (const change of item.changes) {
      if (change.path != null) lines.push(node(change.path));
      if (change.diff == null || change.diff === "") continue;
      for (const raw of change.diff.split("\n")) {
        if (raw.startsWith("+")) lines.push(added(raw));
        else if (raw.startsWith("-")) lines.push(removed(raw));
        else if (raw.startsWith("@@")) lines.push(meta(raw));
        else lines.push(raw);
      }
    }
    return lines;
  }

  const PLAN_GLYPHS: Record<string, string> = {
    completed: "▣",
    inProgress: "◐",
    pending: "▢",
  };

  function $response(unit: HarnessUnit, index: number): Option<ChatMessage> {
    const content: ChatMessage["content"] = [];
    for (const item of unit.items) {
      switch (item.kind) {
        case "message":
          content.push({ type: "text", text: item.text });
          break;
        case "tool-call": {
          // Semantic, not pre-rendered: the surface presents a tool call
          // the way the native agent apps do (condensed entry, output on
          // expand), which it can only do from data.
          content.push({
            type: "tool_call",
            tool: item.tool,
            command: item.command ?? item.tool ?? "(tool call)",
            output: item.output,
            status: item.status,
          });
          break;
        }
        case "diff": {
          // Some wire records fold to nothing (a diff id whose last record
          // carried no changes) — an empty block is noise, not fidelity.
          const lines = diffLines(item);
          if (lines.length > 0) {
            content.push({ type: "terminal", lines, replay: false });
          }
          break;
        }
        case "plan":
          content.push({
            type: "text",
            text: item.steps
              .map((s) => `${PLAN_GLYPHS[s.status ?? ""] ?? "▢"} ${s.step ?? ""}`)
              .join("\n"),
          });
          break;
      }
    }
    // A unit with no response items (a /compact, an interrupt) contributes
    // only its prompt — nothing is invented to stand in for a response.
    if (content.length === 0) return null;
    return {
      id: `u${unit.unit}-response`,
      role: "assistant",
      content,
      isStreaming: false,
      createdAt: unit.ts ? Date.parse(unit.ts) + 1 : index,
    };
  }

  function harnessMessages(units: HarnessUnit[]): ChatMessage[] {
    const messages: ChatMessage[] = [];
    units.forEach((unit, i) => {
      messages.push($prompt(unit, i));
      const $r = $response(unit, i);
      if ($r) messages.push($r);
    });
    return messages;
  }

  /** The whole captured slice: units 274–318, ten real working turns. */
  export function session(): ChatMessage[] {
    return harnessMessages(HARNESS_14925.units);
  }

  /**
   * The whole long-running session (rwc-01's "legacy-replay" 14925): every
   * unit that carries harness content, folded with the full-session caps —
   * the real-data stress the playground exists for.
   */
  export function full(): ChatMessage[] {
    return harnessMessages(HARNESS_14925_FULL.units);
  }

  /** One deep unit (285: 8 messages, 56 tool calls, 50 diffs) — a single-cell stress. */
  export function unit285(): ChatMessage[] {
    return harnessMessages(HARNESS_14925.units.filter((u) => u.unit === 285));
  }

  /**
   * The rendered wire, canonicalized: the rows tsh painted, folded back to
   * ACP-shaped messages by the render's own grammar. `── unit N · T ──`
   * opens a unit (and becomes message identity), `›` opens the prompt and
   * `•` the summary — their hard-wrapped continuation rows rejoin into the
   * verbatim prose the renderer wrapped — `$` opens a terminal block that
   * keeps its raw SGR rows, and blank rows separate paragraphs.
   */
  export function rendered(): ChatMessage[] {
    return canonicalize(INSPECTOR_CODEX_HISTORY);
  }

  type Section = {
    unit: Option<number>;
    role: "user" | "assistant";
    blocks: ChatMessage["content"];
  };

  export function canonicalize(rows: string[]): ChatMessage[] {
    const messages: ChatMessage[] = [];
    let section: Option<Section> = null;
    let para: Option<string> = null;
    let term: Option<string[]> = null;
    let unit: Option<number> = null;

    const flushPara = () => {
      if (section && para != null && para.length > 0) {
        section.blocks.push({ type: "text", text: para });
      }
      para = null;
    };
    const flushTerm = () => {
      if (section && term != null && term.length > 0) {
        section.blocks.push({ type: "terminal", lines: term, replay: false });
      }
      term = null;
    };
    const flushSection = () => {
      flushPara();
      flushTerm();
      if (section && section.blocks.length > 0) {
        messages.push({
          id: `r${section.unit ?? "x"}-${section.role}-${messages.length}`,
          role: section.role,
          content: section.blocks,
          isStreaming: false,
          createdAt: messages.length,
        });
      }
      section = null;
    };
    const open = (role: "user" | "assistant") => {
      if (!section || section.role !== role) {
        flushSection();
        section = { unit, role, blocks: [] };
      } else {
        flushPara();
        flushTerm();
      }
    };

    for (const raw of rows) {
      const text = TermRow.parse(raw).text;
      const s = text.trimStart();
      if (s.startsWith("── unit")) {
        flushSection();
        const m = s.match(/^── unit (\d+)/);
        unit = m ? Number(m[1]) : null;
        continue;
      }
      if (s.length === 0) {
        flushPara();
        flushTerm();
        continue;
      }
      if (s.startsWith("›")) {
        open("user");
        para = s.slice(1).trimStart();
        continue;
      }
      if (s.startsWith("•")) {
        open("assistant");
        para = s.slice(1).trimStart();
        continue;
      }
      if (s.startsWith("$")) {
        open("assistant");
        flushPara();
        term = term ?? [];
        term.push(raw);
        continue;
      }
      if (term != null) {
        term.push(raw);
        continue;
      }
      if (para != null) {
        // A continuation row of hard-wrapped prose: rejoin what the
        // renderer's column width broke.
        para += " " + s;
        continue;
      }
      // A row with no opening grammar — a scene, a table — is terminal
      // content by nature: keep it as painted.
      open("assistant");
      term = [raw];
    }
    flushSection();
    return messages;
  }
}
